import { Injectable, inject } from "@angular/core";
import type { PutObjectCommandInput, S3Client } from "@aws-sdk/client-s3";
import { firstValueFrom } from "rxjs";
import type {
	CreateFloorplanInput,
	CreateImageInput,
	Floorplan,
	Image,
} from "src/app/schema";
import { FloorplanService, ImageService } from "src/app/services";
import { environment } from "src/environments/environment";

@Injectable({
	providedIn: "root",
})
export class ContentUploadService {
	private readonly _imageService = inject(ImageService);
	private readonly _floorplanService = inject(FloorplanService);

	private _s3Client: S3Client | undefined;

	public async uploadAndCreateImage(
		file: File,
		image: CreateImageInput,
	): Promise<Image> {
		if (!image.id) {
			throw new Error("Image ID is required.");
		}
		if (!image.property?.id) {
			throw new Error("Property ID is required.");
		}
		const [_, createdImage] = await Promise.all([
			this.uploadImage(file, image.id, image.property.id),
			firstValueFrom(this._imageService.createImage(image)),
		]);
		return createdImage;
	}

	public async uploadAndCreateFloorplan(
		file: File,
		floorplan: CreateFloorplanInput,
	): Promise<Floorplan> {
		if (!floorplan.id) {
			throw new Error("Floorplan ID is required.");
		}
		if (!floorplan.property?.id) {
			throw new Error("Property ID is required.");
		}
		const [_, createdFloorplan] = await Promise.all([
			this.uploadFloorplan(file, floorplan.id, floorplan.property.id),
			firstValueFrom(this._floorplanService.createFloorplan(floorplan)),
		]);
		return createdFloorplan;
	}

	public async uploadImage(
		file: File,
		imageId: string,
		propertyId: string,
	): Promise<unknown> {
		const fileArrayBuffer = await file.arrayBuffer();
		const uint8Array = new Uint8Array(fileArrayBuffer);

		const params: PutObjectCommandInput = {
			Bucket: "leadreel-images",
			Key: `${propertyId}/${imageId}`,
			Body: uint8Array, // Use Uint8Array instead of File
			ACL: "private",
			ContentType: file.type,
		};
		const { PutObjectCommand } = await import("@aws-sdk/client-s3");
		const command = new PutObjectCommand(params);
		const client = await this.getS3Client();
		return client.send(command);
	}

	public async uploadFloorplan(
		file: File,
		floorplanId: string,
		propertyId: string,
	): Promise<unknown> {
		const fileArrayBuffer = await file.arrayBuffer();
		const uint8Array = new Uint8Array(fileArrayBuffer);

		const params: PutObjectCommandInput = {
			Bucket: "leadreel-floorplans",
			Key: `${propertyId}/${floorplanId}`,
			Body: uint8Array,
			ACL: "private",
			ContentType: file.type,
		};
		const { PutObjectCommand } = await import("@aws-sdk/client-s3");
		const command = new PutObjectCommand(params);
		const client = await this.getS3Client();
		return client.send(command);
	}

	private async getS3Client(): Promise<S3Client> {
		if (!this._s3Client) {
			const { S3Client } = await import("@aws-sdk/client-s3");
			this._s3Client = new S3Client({
				// TODO: Upload via links generated by the server instead of client-side credentials
				credentials: {
					accessKeyId: environment.uploadContentCredentials.accessKey,
					secretAccessKey: environment.uploadContentCredentials.secretKey,
				},
				region: "us-east-1",
			});
		}
		return this._s3Client;
	}
}
